import React, {useState} from 'react';
import {IImageDimension} from '../../../../types/productDef';
import classNames from 'classnames';
import style from './ProductVideoItem.scss';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {IVideoFile} from '@wix/wixstores-graphql-schema/dist/es/src';
import loadable from '@wix/yoshi-flow-editor/loadable';
import {IMediaItem} from '../../../../types/app-types';
import {ReactPlayableProps} from 'react-playable/dist/src';
import {getMediaUrl} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';

export interface ProductVideoProps {
  video: IVideoFile;
  dimensions: IImageDimension;
  index: number;
  onAddVideo(player: any, index: number): any;
  posterUrl?: string;
  mediaItem?: IMediaItem;
  imageMode?: ImageMode;
  mountedToDOM?: boolean;
}

export enum ProductVideoItemDataHook {
  Video = 'product-page-media-video',
  Poster = 'product-page-media-video-poster',
}

const ReactPlayable = loadable<ReactPlayableProps>(
  () => import(/* webpackChunkName: "ReactPlayable" */ 'react-playable/dist/src'),
  {
    resolveComponent: (components) => components.ReactPlayable,
  }
);

const getLegacyPosterUrl = (mediaItem: IMediaItem, dimensions: IImageDimension, imageMode?: ImageMode): string => {
  return getMediaUrl(mediaItem, dimensions, {
    isSSR: false,
    imageMode,
  });
};

export const ProductVideoItem: React.FunctionComponent<ProductVideoProps> = ({
  video,
  dimensions,
  index,
  posterUrl,
  onAddVideo,
  mediaItem,
  imageMode,
}) => {
  const [playerReady, setPlayerReady] = useState(false);
  const WIXVIDEO_BASEURL = 'https://wixvideo.cdn.inventica.ru/';
  const videoContainerClasses = classNames([style.media, style.mediaVideo]);

  // Use optimized posterUrl if provided, otherwise compute legacy poster URL
  const isOptimizedFlow = !!posterUrl;
  const effectivePosterUrl = posterUrl || (mediaItem ? getLegacyPosterUrl(mediaItem, dimensions, imageMode) : '');

  const handlePlayerInit = (player: any) => {
    setPlayerReady(true);
    onAddVideo(player, index);
  };

  if (isOptimizedFlow) {
    return (
      <div
        data-hook={ProductVideoItemDataHook.Video}
        className={videoContainerClasses}
        style={{height: dimensions.height, width: dimensions.width}}>
        {/* Poster shown immediately (URL computed in SSR), hidden when player ready */}
        {!playerReady && (
          <img
            data-hook={ProductVideoItemDataHook.Poster}
            src={effectivePosterUrl}
            width={dimensions.width}
            height={dimensions.height}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            alt=""
            {...{fetchpriority: 'high'}}
          />
        )}
        {/* Player always mounted (to trigger onInit), hidden until ready */}
        <div className={classNames(style.mediaContent, {[style.hidden]: !playerReady})}>
          <ReactPlayable
            onInit={handlePlayerInit}
            width={dimensions.width}
            height={dimensions.height}
            src={`${WIXVIDEO_BASEURL}${video.url}`}
            poster={effectivePosterUrl}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      data-hook={ProductVideoItemDataHook.Video}
      className={videoContainerClasses}
      style={{height: dimensions.height, width: dimensions.width}}>
      <ReactPlayable
        onInit={(player: any) => onAddVideo(player, index)}
        width={dimensions.width}
        height={dimensions.height}
        src={`${WIXVIDEO_BASEURL}${video.url}`}
        poster={effectivePosterUrl}
      />
    </div>
  );
};
